/**
 * /relatorio-ca — F-bank-sync — Relatório do GAP de CA (HT).
 *
 * Ataca a diferença bilan × Gestion (ex.: 23.247 €). Tudo em HT (a base do
 * lucro). Read-only. Consome `treasury.caGap`. O astro é a lista de créditos
 * no banco SEM fatura casada — os suspeitos de venda sub-faturada.
 *
 * Estilo nativo do módulo fiscal (FiscalCard/KpiCard/FiscalYearSelector).
 */
import { FiscalYearSelector } from "../components/fiscal/FiscalYearSelector.js";
import { KpiCard } from "../components/fiscal/KpiCard.js";
import { FiscalCard } from "../components/fiscal/Shell.js";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useFiscalSelection } from "../hooks/useFiscalSelection.js";
import { useT } from "../i18n/index.js";
import { formatMoney } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";

function monthLabel(ym: string): string {
  // "2026-06" → "06/2026"
  const [y, m] = ym.split("-");
  return m && y ? `${m}/${y}` : ym;
}

export default function RelatorioCaGap() {
  const { companyId, fiscalYearId, setCompanyId, setFiscalYearId } = useFiscalSelection();
  const t = useT();

  const q = trpc.treasury.caGap.useQuery(
    { companyId: companyId ?? 0, fiscalYearId: fiscalYearId ?? 0 },
    { enabled: companyId !== null && fiscalYearId !== null, retry: false },
  );
  const d = q.data;
  const suspeitos = d?.creditosSemFatura;

  return (
    <ErpShell active="gap-ca">
      <div className="mb-5">
        <p className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
          {t("reportCaGap.eyebrow")}
        </p>
        <FiscalYearSelector
          companyId={companyId}
          fiscalYearId={fiscalYearId}
          onChangeCompany={setCompanyId}
          onChangeFiscalYear={(fy) => setFiscalYearId(fy?.id ?? null)}
        />
      </div>

      {companyId === null || fiscalYearId === null ? (
        <FiscalCard>
          <p className="text-sm text-neutral-500">{t("reportCaGap.selectPrompt")}</p>
        </FiscalCard>
      ) : q.isLoading ? (
        <FiscalCard>
          <p className="text-sm text-neutral-500">{t("reportCaGap.loading")}</p>
        </FiscalCard>
      ) : q.error ? (
        <FiscalCard>
          <p className="text-sm text-red-400">{q.error.message}</p>
        </FiscalCard>
      ) : d ? (
        <div className="space-y-6">
          {/* Headline */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label={t("reportCaGap.kpiCaInvoiced")} value={formatMoney(d.totals.caHt)} />
            <KpiCard
              label={t("reportCaGap.kpiReceived")}
              value={formatMoney(d.totals.caHtPago)}
              tone="positive"
            />
            <KpiCard label={t("reportCaGap.kpiOpen")} value={formatMoney(d.totals.caHtAberto)} />
            <KpiCard
              label={t("reportCaGap.kpiCreditsNoInvoice")}
              value={formatMoney(suspeitos?.total ?? 0)}
              subtitle={`${suspeitos?.count ?? 0} ${t("reportCaGap.entriesUnit")}`}
              tone={(suspeitos?.total ?? 0) > 0.005 ? "amber" : "neutral"}
            />
          </div>

          {/* O caçador dos 23k */}
          <FiscalCard
            title={t("reportCaGap.creditsCardTitle")}
            subtitle={t("reportCaGap.creditsCardSubtitle")}
            padded={false}
          >
            {!suspeitos || suspeitos.count === 0 ? (
              <p className="p-5 text-sm text-lime">{t("reportCaGap.creditsEmpty")}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase text-neutral-500 border-b border-neutral-800">
                    <tr>
                      <th className="px-4 py-2">{t("reportCaGap.colDate")}</th>
                      <th className="px-4 py-2 text-right">{t("reportCaGap.colAmount")}</th>
                      <th className="px-4 py-2">{t("reportCaGap.colDescription")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suspeitos.items.map((it, i) => (
                      <tr key={i} className="border-b border-neutral-900">
                        <td className="px-4 py-1.5 whitespace-nowrap tabular-nums">{it.date}</td>
                        <td className="px-4 py-1.5 text-right tabular-nums text-amber-300">
                          {formatMoney(it.amount)}
                        </td>
                        <td className="px-4 py-1.5 text-neutral-400">{it.description ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-neutral-700 font-semibold">
                      <td className="px-4 py-2">
                        {t("reportCaGap.total")}
                        {suspeitos.capped ? ` ${t("reportCaGap.totalCapped")}` : ""}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-amber-300">
                        {formatMoney(suspeitos.total)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </FiscalCard>

          {/* CA por mês */}
          <FiscalCard title={t("reportCaGap.monthlyCardTitle")} padded={false}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-neutral-500 border-b border-neutral-800">
                  <tr>
                    <th className="px-4 py-2">{t("reportCaGap.colMonth")}</th>
                    <th className="px-4 py-2 text-right">{t("reportCaGap.colCaHt")}</th>
                    <th className="px-4 py-2 text-right">{t("reportCaGap.colReceivedHt")}</th>
                    <th className="px-4 py-2 text-right">{t("reportCaGap.colOpenHt")}</th>
                    <th className="px-4 py-2 text-right">{t("reportCaGap.colTvaCollected")}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.months.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-5 text-center text-neutral-500">
                        {t("reportCaGap.monthlyEmpty")}
                      </td>
                    </tr>
                  ) : (
                    d.months.map((m) => (
                      <tr key={m.month} className="border-b border-neutral-900">
                        <td className="px-4 py-1.5 tabular-nums">{monthLabel(m.month)}</td>
                        <td className="px-4 py-1.5 text-right tabular-nums">
                          {formatMoney(m.caHt)}
                        </td>
                        <td className="px-4 py-1.5 text-right tabular-nums text-lime">
                          {formatMoney(m.caHtPago)}
                        </td>
                        <td className="px-4 py-1.5 text-right tabular-nums text-neutral-400">
                          {formatMoney(m.caHtAberto)}
                        </td>
                        <td className="px-4 py-1.5 text-right tabular-nums text-neutral-400">
                          {formatMoney(m.tvaCollectee)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </FiscalCard>

          {/* Painel TVA */}
          <FiscalCard
            title={t("reportCaGap.tvaCardTitle")}
            subtitle={t("reportCaGap.tvaCardSubtitle")}
          >
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-500">
                  {t("reportCaGap.tvaCollected")}
                </div>
                <div className="text-lg font-semibold tabular-nums">
                  {formatMoney(d.tva.collectee)}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-500">
                  {t("reportCaGap.tvaDeductible")}
                </div>
                <div className="text-lg font-semibold tabular-nums">
                  {formatMoney(d.tva.deductible)}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-500">
                  {t("reportCaGap.tvaDue")}
                </div>
                <div className="text-lg font-semibold tabular-nums text-amber-300">
                  {formatMoney(d.tva.due)}
                </div>
              </div>
            </div>
          </FiscalCard>

          <p className="text-xs text-neutral-600">{t("reportCaGap.methodology")}</p>
        </div>
      ) : null}
    </ErpShell>
  );
}
